<template>
  <mark
    ref="inputRef"
    contenteditable="true"
    autocorrect="off"
    autocapitalize="none"
    autocomplete="off"
    class="text-blue-600 transparent-input"
    @keydown.capture="handleKeydown"
    @keyup.stop
    @paste.capture="handlePaste"
    @input="handleInput"
  >
  </mark>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import type { EditorView } from '@tiptap/pm/view';
import type { Transaction } from '@tiptap/pm/state';
import { TextSelection, PluginKey } from '@tiptap/pm/state';
import { onClickOutside } from '@vueuse/core';

const props = defineProps<{
  storeDispatch: any;
  view: EditorView;
  getPos: () => number;
  query: string;
  pluginKey: PluginKey;
  prefix: string;
}>();

const inputRef = ref<HTMLElement | null>(null);

declare global {
  interface Window {
    StVueApp: any;
  }
}

let lastOffset = 0;

type AbortSuggestionProps = {
  prefix?: string;
  destroy?: boolean;
  appendTransaction?: (tr: Transaction, pos: number) => void;
};

const abortSuggestionDefaults: AbortSuggestionProps = {
  prefix: '@',
  destroy: false,
  appendTransaction: () => {},
};

function abortSuggestion({ prefix, destroy, appendTransaction } = abortSuggestionDefaults) {
  const pos = props.getPos?.();
  if (inputRef.value && pos) {
    const tr = props.view.state.tr;

    if (!destroy) {
      const text = `${prefix}${inputRef.value.innerText}`;
      tr.insertText(text, pos);
    }

    tr.setMeta(props.pluginKey, {
      type: 'remove',
    });

    appendTransaction?.(tr, pos);

    props.view.dispatch(tr);

    props.view.focus();

    destroyComponent();
  }
}

onClickOutside(inputRef, (e: MouseEvent) => {
  /**
   * If anything besides the menu mention was clicked, abort.
   */
  if (!(e.target as Element).closest('.mention-items')) {
    abortSuggestion({ prefix: props.prefix });
    return;
  }

  /**
   * If the mention item was clicked, destroy component.
   */
  if ((e.target as Element).closest('.search-results__group-item-content')) {
    destroyComponent();
    return;
  }

  /**
   * If the mention menu was clicked, retain focus.
   */

  /**
   * If the query is empty, just focus the input.
   */
  if (props.query === '') {
    inputRef?.value?.focus();
    return;
  }

  /**
   * If the query is not empty, focus the input and move the cursor to the end.
   * NOTE: To keep the cursor position in safari, we need to work with ranges.
   * Incidentally, this works for other browsers as well.
   */
  const sel = window.getSelection();
  const range = document.createRange();
  if (inputRef?.value?.childNodes?.[0]) {
    range.setStart(inputRef?.value.childNodes[0], lastOffset);
    range.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(range);
    inputRef?.value?.focus();
  }
});

onMounted(() => {
  window.addEventListener('popstate', handlePopstate);
  props.storeDispatch('setSuggesterShown', true);
  inputRef?.value?.focus();

  if (inputRef?.value) {
    inputRef.value.textContent = props.query;
    const range = document.createRange();

    // Select the end of the contentEditable element
    range.selectNodeContents(inputRef.value);
    range.collapse(false);

    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  const tr = props.view.state.tr;

  tr.setMeta(props.pluginKey, {
    active: true,
  });

  props.view.dispatch(tr);

  // add context menu listener
  document.addEventListener('contextmenu', onContextMenu);
});

onBeforeUnmount(() => {
  document.removeEventListener('contextmenu', onContextMenu);
  props.storeDispatch('setSuggesterShown', false);
});

function onContextMenu(e: MouseEvent) {
  if (e.button === 2) {
    // Right-click detected
    abortSuggestion({ prefix: props.prefix });
  }
}

function destroyComponent() {
  window.removeEventListener('popstate', handlePopstate);
}

function handleInput() {
  requestAnimationFrame(() => {
    const content = inputRef.value?.textContent;
    if (content?.match(/  |^ $/)) {
      return abortSuggestion({ prefix: props.prefix });
    }

    const tr = props.view.state.tr;

    tr.setMeta(props.pluginKey, {
      type: 'update',
      query: content,
    });

    props.view.dispatch(tr);

    // set selection to the end of the input
    if (inputRef.value) {
      const range = document.createRange();
      range.selectNodeContents(inputRef.value);
      range.collapse(false);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  });
}

function handleKeydown(e: KeyboardEvent) {
  const selection = window.getSelection();
  requestAnimationFrame(() => {
    lastOffset = window.getSelection()?.anchorOffset || 0;
  });

  if (
    e.key === 'Backspace' &&
    selection?.anchorNode?.parentNode === inputRef.value &&
    selection?.anchorOffset === 0
  ) {
    abortSuggestion({
      prefix: '',
      appendTransaction: (tr, pos) => {
        tr.setSelection(TextSelection.create(tr.doc, pos));
      },
    });

    /**
     *? This is necessary, in order to prevent the Backspace
     *? deleting one character before the mention.
     */
    e.preventDefault();
  }

  if (e.key === 'Backspace' && inputRef.value?.textContent === '') {
    abortSuggestion({
      prefix: '',
    });

    /**
     *? This is necessary, in order to prevent the Backspace
     *? deleting one character before the mention.
     */
    e.preventDefault();
  }

  if (['ArrowLeft', 'ArrowRight'].includes(e.key)) {
    const queryLength = inputRef?.value?.textContent?.length || 0;

    if (
      selection?.anchorNode?.parentNode === inputRef.value ||
      selection?.anchorNode?.parentElement?.closest('typeahead')
    ) {
      let offset = 0;
      let edgeDetected = false;

      if (e.key === 'ArrowRight') {
        if (selection?.anchorOffset === queryLength) {
          /**
           * We add 1 to account for the prefix length.
           * We do not need prevent default here, since the arrow right behavior is
           * desirable.
           */
          offset = queryLength + props.prefix.length;
          e.preventDefault();
          edgeDetected = true;
        } else if (e.ctrlKey || e.altKey) {
          if (inputRef.value?.textContent?.slice(selection?.anchorOffset).match(/^\s*\w+$/g)) {
            offset = queryLength + props.prefix.length;
            e.preventDefault();
            edgeDetected = true;
          }
        }
      } else if (e.key === 'ArrowLeft') {
        if (selection?.anchorOffset === 0) {
          /**
           * Prevent default is required here, since we're setting the position
           * right at the start of the decoration, which (if accounting for
           * a one-character prefix) is correct.
           */
          offset = props.prefix.length - 1;
          e.preventDefault();
          edgeDetected = true;
        } else if (e.ctrlKey || e.altKey) {
          /**
           * Ctrl + Arrow Left is used to jump. The bottom regex
           * matches words like "this" and "this ". Ctrl + Arrow Left
           * in these instances would make the cursor land outside.
           */
          if (inputRef.value?.textContent?.slice(0, selection?.anchorOffset).match(/^\w+\s*$/g)) {
            e.preventDefault();
            edgeDetected = true;
          }
        } else if (e.metaKey) {
          abortSuggestion({
            prefix: props.prefix,
            appendTransaction: (tr, pos) => {
              tr.setSelection(TextSelection.create(tr.doc, pos));
            },
          });
        }
      }

      if (edgeDetected) {
        abortSuggestion({
          prefix: props.prefix,
          appendTransaction: (tr, pos) => {
            tr.setSelection(TextSelection.create(tr.doc, pos + offset));
          },
        });
      }
    }
  }

  if (['ArrowDown', 'ArrowUp', 'Tab', 'Enter'].includes(e.key)) {
    e.preventDefault();
    if (['Enter', 'Tab'].includes(e.key)) {
      // destroy component when item is selected with enter or tab
      destroyComponent();
    }
    return;
  }

  if (e.key === 'Delete') {
    const queryLength = inputRef?.value?.textContent?.length || 0;

    if (selection?.anchorOffset === queryLength) {
      abortSuggestion({
        prefix: props.prefix,
      });
    }
  }

  if (e.key === 'Escape') {
    abortSuggestion({
      prefix: props.prefix,
    });
  }

  if (e.ctrlKey || e.metaKey) {
    if (e.key === 'z') {
      e.preventDefault();
      abortSuggestion({
        destroy: true,
      });
    }

    if (e.key === 'a') {
      abortSuggestion({
        prefix: props.prefix,
      });
    }
  }

  e.stopPropagation();
}

function handlePaste(e: ClipboardEvent) {
  e.stopImmediatePropagation();
  e.stopPropagation();
  e.preventDefault();

  const text = e.clipboardData?.getData('text');

  if (inputRef.value && text) {
    const sel = window.getSelection();

    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const textNode = document.createTextNode(text);
      range.insertNode(textNode);

      // Move the cursor to the end of the inserted content
      range.setStartAfter(textNode);
      range.collapse(true);

      sel.removeAllRanges();
      sel.addRange(range);
    }

    // Trigger the change event to Vue's reactivity system
    inputRef.value.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

function handlePopstate() {
  abortSuggestion({ prefix: props.prefix });
}
</script>

<style lang="scss">
.transparent-input {
  /* Resets */
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  width: auto;

  /* Inherits */
  background: transparent;
  font-size: inherit;
  font-family: inherit;
  color: inherit;
}
div.edit-page:not(.editor-full-width) {
  .ProseMirror > typeahead {
    width: 700px;
    margin-left: auto;
    margin-right: auto;
    padding-top: 6px;
    padding-bottom: 6px;
  }
}
.ProseMirror-separator {
  opacity: 0;
}
</style>
